import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { QuoteService } from '../../quote.service';
import { EquineService } from '../equine.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from "@angular/router";
import { NgForm } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GridOptions } from "ag-grid-community";
import { formatDate } from '@angular/common';
import { normalizeEquineQuotedReferralState, wasEquineQuotePreviouslyQuoted } from '../equine-premium-tbc.util';
import { requireEquineQuoteId, resolveEquinePreviousPolicyId } from '../equine-utils';

@Component({
  selector: 'app-equine-statement-of-fact',
  templateUrl: './statement-of-facts.component.html',
  styleUrls: ['./statement-of-facts.component.css', '../../quote.component.css']
})
export class StatementOfFactsComponent implements OnInit, OnChanges {
  @Input() modelObject: any = {};
  @Input() staticData: any;
  @Output() modelObjectUpdated = new EventEmitter<any>();
  @Output() BackToClaimsTab = new EventEmitter();
  @Output() GoToPremiumSummary = new EventEmitter();

  @ViewChild('sofForm') sofForm: NgForm;

  public quote: any = {};
  public generalStatementOfFacts: any[] = [];
  public veterinaryStatementOfFacts: any[] = [];
  public declarationStatementOfFacts: any[] = [];
  public rowData: any = [];
  public submitted = false;
  public isSavingSof = false;

  // Document upload properties
  docTitle: string = '';
  fileToUpload: File = null;
  documentsEditMode: boolean = false;
  documentID: string = '';
  helptext: string = '';

  // Grid options
  gridOptions: GridOptions = {
    onGridReady: () => {
      // Grid ready callback
    }
  };

  columnDefs = [
    { headerName: 'Document Name', field: 'title', sortable: true, width: 300 },
    { headerName: 'Document Version', field: 'quote_version', sortable: true },
    {
      headerName: 'Creation Date', sortable: true, filter: true,
      cellRenderer: function (params) {
        return formatDate(params.data.created_date, 'dd-MM-yyyy', 'en-US');
      }
    },
    {
      headerName: null,
      width: 130,
      cellRenderer: function (params) {
        let links = '<a title="Edit Documents" style="font-size: 18px;"><i class="mdi mdi-grease-pencil" data-action-type="edit"></i></a>';
        links += '<a class="ml-2" title="Download Document" style="font-size: 18px;color: #138a51;"><i class="mdi mdi-cloud-download" data-action-type="download"></i></a>';
        links += '<a class="ml-2" title="Delete Documents" style="font-size: 18px;"><i class="mdi mdi-close-circle-outline text-danger" data-action-type="delete"></i></a>';
        return links;
      }
    }
  ];

  constructor(
    private quoteService: QuoteService,
    private equineService: EquineService,
    private _snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private el: ElementRef
  ) { }

  ngOnInit(): void {
    this.quote = this.modelObject;
    this.loadStatementOfFacts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['modelObject'] && changes['modelObject'].currentValue) {
      this.quote = this.modelObject;
      
      // Initialize statement_of_facts if not present
      if (!this.quote.statement_of_facts) {
        this.quote.statement_of_facts = [];
      }

      // Reset response values from 0 to null
      this.quote.statement_of_facts.forEach((res: any) => {
        if (res.response == 0) {
          res.response = null;
        }
      });
      this.quote.statement_of_facts = this.dedupeStatementOfFacts(this.quote.statement_of_facts);

      this.loadStatementOfFacts();

      // Load documents if quote has id
      if (this.quote.id) {
        this.getDocuments();
      }
    }
  }

  public loadStatementOfFacts(): void {
    if (!this.quote) {
      return;
    }
    if (!this.quote.sof_version) {
      this.quote.sof_version = (this.staticData && this.staticData.sof_version) ? this.staticData.sof_version : 1;
    }

    const coverTypeId = this.quote?.policy_cover_details?.cover_type_id ?? this.quote?.cover_type_id ?? undefined;
    const isLossOfHorse = Number(this.quote?.policy_cover_details?.is_loss_of_horse_selected ?? this.quote?.is_loss_of_horse_selected ?? 0);
    const isMort = (isLossOfHorse === 1) ? 1 : undefined;
    this.equineService.getStatementOfFactsByCoverType(coverTypeId, isMort)
      .subscribe(
        (response: any) => {
          if (response.success) {
            const result = response.result;

            if (result && !Array.isArray(result) && (result.general_sof || result.veterinary_sof)) {
              this.generalStatementOfFacts = Array.isArray(result.general_sof) ? result.general_sof : [];
              this.veterinaryStatementOfFacts = Array.isArray(result.veterinary_sof) ? result.veterinary_sof : [];
              this.declarationStatementOfFacts = this.generalStatementOfFacts.filter((res: any) => res.is_declaration == 1);
              this.generalStatementOfFacts = this.generalStatementOfFacts.filter((res: any) => res.is_declaration != 1);
            } else {
              const list = Array.isArray(result)
                ? result
                : (result?.sof_ques ?? result?.statement_of_fact ?? response.sof_ques);
              this.generalStatementOfFacts = [];
              this.veterinaryStatementOfFacts = Array.isArray(list) ? list : [];
              this.declarationStatementOfFacts = this.generalStatementOfFacts.filter((res: any) => res.is_declaration == 1);
            }

            const allQuestions = [...this.generalStatementOfFacts, ...this.veterinaryStatementOfFacts, ...this.declarationStatementOfFacts];

            if (!this.quote.statement_of_facts || !Array.isArray(this.quote.statement_of_facts)) {
              this.quote.statement_of_facts = [];
            }

            allQuestions.forEach((res: any) => {
              const exists = this.quote.statement_of_facts.some((x: any) => x.question_id == res.id);
              if (!exists) {
                this.quote.statement_of_facts.push({
                  id: '',
                  quote_id: '',
                  question_id: res.id,
                  response: null,
                  response_bool: false,
                  response_text: '',
                  display_order: res.display_order
                });
              }
            });
            this.quote.statement_of_facts = this.dedupeStatementOfFacts(this.quote.statement_of_facts);
          }
        },
        (error) => {
          console.error('Error loading statement of facts:', error);
        }
      );
  }

  getDocuments(): void {
    if (this.quote.id) {
      this.quoteService.getQuoteDocuments(this.quote.id)
        .subscribe(
          (response: any) => {
            if (response.success) {
              let data: any[] = [];
              response.result.forEach((res: any) => {
                if (res.is_static == 0) {
                  data.push(res);
                }
              });
              this.rowData = data;
            }
          },
          (error) => {
            console.error('Error loading documents:', error);
          }
        );
    }
  }

  helpTextPopup(content: any, text: string): void {
    const modalRef = this.modalService.open(content, { centered: true });
    this.helptext = text;
  }

  uploadDocumentBtn(documents: any): void {
    this.documentID = '';
    this.docTitle = '';
    this.fileToUpload = null;
    this.documentsEditMode = false;
    this.quote.quote_document = '';
    this.modalService.open(documents, { centered: true });
  }

  getUploadedFile(files: FileList): void {
    if (files && files.length > 0) {
      this.fileToUpload = files.item(0);
      this.quote.quote_document = this.fileToUpload ? this.fileToUpload.name : '';
    }
  }

  documentUpload(): void {
    if (!this.fileToUpload || !this.docTitle) {
      this._snackBar.open('Please provide both title and document', 'Close', {
        duration: 3000,
        panelClass: 'text-danger'
      });
      return;
    }

    let formData = new FormData();
    formData.append("file_name", this.fileToUpload);
    formData.append("quote_id", this.quote.id);
    formData.append("broker_id", this.quote.broker_id || '');
    formData.append("title", this.docTitle);
    formData.append('is_uploaded_manually', '1');

    this.quoteService.saveQuoteDocument(formData)
      .subscribe(
        (response: any) => {
          this.modalService.dismissAll();
          if (response.success) {
            this.quote.quote_document = response.result.file_name;
            this.getDocuments();
            this._snackBar.open('Document uploaded.', 'Close', {
              duration: 5000,
            });
          } else {
            this._snackBar.open('Unable to upload document.', 'Close', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        (error) => {
          this._snackBar.open('Error uploading document.', 'Close', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      );
  }

  onGridRowClicked(e: any, documents: any, documentsDelete: any): void {
    if (e && e.event && e.event.target) {
      let actionType = e.event.target.getAttribute("data-action-type");
      this.documentID = e.data.id;
      this.docTitle = e.data.title;
      
      switch (actionType) {
        case "download":
          this.quoteService.downloadDocs(e.data);
          break;
        case "edit":
          this.documentsEditMode = true;
          this.modalService.open(documents, { centered: true });
          break;
        case "delete":
          this.modalService.open(documentsDelete, { centered: true });
          break;
      }
    }
  }

  updateDocument(): void {
    let data: any = {
      document_id: this.documentID,
      title: this.docTitle
    };
    
    this.quoteService.updateDocument(data)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.modalService.dismissAll();
            this.rowData.forEach((res: any) => {
              if (this.documentID == res.id) {
                res.title = this.docTitle;
              }
            });
            if (this.gridOptions.api) {
              this.gridOptions.api.setRowData(this.rowData);
              this.gridOptions.api.refreshCells();
            }
            this._snackBar.open(response.message, 'Close', {
              duration: 5000,
            });
          } else {
            this._snackBar.open('Unable to update title. Please try again later.', 'Close', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        (error) => {
          this._snackBar.open('Error updating document.', 'Close', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      );
  }

  deleteDocument(): void {
    let data: any = {
      document_id: this.documentID
    };
    
    this.quoteService.deleteDocument(data)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.modalService.dismissAll();
            const selectedRow = this.gridOptions.api?.getFocusedCell();
            if (selectedRow && this.gridOptions.rowData) {
              const rowIndex = selectedRow.rowIndex;
              this.gridOptions.rowData.splice(rowIndex, 1);
              if (this.gridOptions.api) {
                this.gridOptions.api.setRowData(this.gridOptions.rowData);
              }
            }
            this._snackBar.open(response.message, 'Close', {
              duration: 5000,
            });
          } else {
            this._snackBar.open(response.message, 'Close', {
              duration: 5000,
              panelClass: 'text-danger'
            });
          }
        },
        (error) => {
          this._snackBar.open('Error deleting document.', 'Close', {
            duration: 5000,
            panelClass: 'text-danger'
          });
        }
      );
  }

  backToClaimsTabButton(): void {
    this.BackToClaimsTab.emit();
  }

  private scrollToFirstInvalid(): void {
    setTimeout(() => {
      const firstInvalid = this.el.nativeElement.querySelector(
        '.ng-invalid:not(form):not([type="hidden"])'
      );
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (firstInvalid.focus) firstInvalid.focus();
      }
    });
  }

  continueToPremiumSummary(): void {
    // if (this.isSavingSof) return;
    this.submitted = true;
    const f = this.sofForm;
    if (!f) {
      return;
    }

    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this._snackBar.open('No quote ID found. Please save Quote Details first.', 'Close', { duration: 3000, panelClass: 'text-danger' });
      return;
    }

    f.form.markAllAsTouched();
    if (!f.form.valid) {
      this._snackBar.open('Please answer all required questions before continuing.', 'Close', { duration: 4000, panelClass: 'text-danger' });
      this.scrollToFirstInvalid();
      return;
    }

    const validQuestionIds = new Set([
      ...this.generalStatementOfFacts.map((q: any) => q.id),
      ...this.veterinaryStatementOfFacts.map((q: any) => q.id),
      ...this.declarationStatementOfFacts.map((q: any) => q.id)
    ]);
    
    const filteredSof = this.dedupeStatementOfFacts(this.quote.statement_of_facts || [])
      .filter((sof: any) => validQuestionIds.has(sof.question_id));

    const data = {
      ...this.modelObject,
      id: quoteId,
      transaction_type: this.modelObject.type,
      statement_of_facts: filteredSof,
      has_form_changed: !!f?.form?.dirty,
      is_quote_referred: !wasEquineQuotePreviouslyQuoted(this.modelObject),
      is_manually_referred: 0,
    };

    const prevPolicyId = this.modelObject.previous_policy_id;
    const txnType = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType = this.modelObject.original_transaction_type;
    this.isSavingSof = true;

    this.equineService.saveQuote(data).subscribe(
      (response: any) => {
        if (response.success) {
          this._snackBar.open('Statement of facts saved. Proceeding to Premium Summary.', 'Close', { duration: 3000 });
          this.modelObject = normalizeEquineQuotedReferralState(response.result);
          this.quote = normalizeEquineQuotedReferralState(response.result);
          if (prevPolicyId) {
            this.modelObject.previous_policy_id = prevPolicyId;
            this.quote.previous_policy_id = prevPolicyId;
          }
          if (txnType) {
            this.modelObject.type = txnType;
            this.quote.type = txnType;
          }
          if (origTxType) {
            this.modelObject.original_transaction_type = origTxType;
            this.quote.original_transaction_type = origTxType;
          }
          this.modelObjectUpdated.emit(this.quote);
          this.GoToPremiumSummary.emit();
        } else {
          this._snackBar.open(response?.message || 'Error saving statement of facts', 'Close', { duration: 5000, panelClass: 'text-danger' });
        }
        this.isSavingSof = false;
      },
      (err: any) => {
        this._snackBar.open(err?.error?.message || 'Error saving statement of facts', 'Close', { duration: 5000, panelClass: 'text-danger' });
        this.isSavingSof = false;
      }
    );
  }

  onSubmit(f: NgForm): void {
    if (this.isSavingSof) return;
    this.submitted = true;

    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this._snackBar.open('No quote ID found. Please save Quote Details first.', 'Close', { duration: 3000, panelClass: 'text-danger' });
      return;
    }

    if (!f.form.valid) {
      f.form.markAllAsTouched();
      this._snackBar.open('Please answer all required questions before continuing.', 'Close', { duration: 4000, panelClass: 'text-danger' });
      this.scrollToFirstInvalid();
      return;
    }

    const validQuestionIds = new Set([
      ...this.generalStatementOfFacts.map((q: any) => q.id),
      ...this.veterinaryStatementOfFacts.map((q: any) => q.id),
      ...this.declarationStatementOfFacts.map((q: any) => q.id)
    ]);
    const filteredSof = this.dedupeStatementOfFacts(this.quote.statement_of_facts || [])
      .filter((sof: any) => validQuestionIds.has(sof.question_id));

    const wasQuoted = wasEquineQuotePreviouslyQuoted(this.modelObject);
    const data = {
      ...this.modelObject,
      id: quoteId,
      transaction_type: this.modelObject.type,
      statement_of_facts: filteredSof,
      has_form_changed: !!f?.form?.dirty,
      previous_policy_id: resolveEquinePreviousPolicyId(this.modelObject),
      is_quote_referred: !wasQuoted,
      is_manually_referred: 0,
    };

    const prevPolicyId2 = this.modelObject.previous_policy_id;
    const txnType2 = this.modelObject.type || this.modelObject.transaction_type;
    const origTxType2 = this.modelObject.original_transaction_type;
    this.isSavingSof = true;
    this.equineService.saveQuote(data)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this._snackBar.open('Statement of facts saved successfully', 'Close', {
              duration: 3000
            });
            this.modelObject = normalizeEquineQuotedReferralState(response.result);
            this.quote = normalizeEquineQuotedReferralState(response.result);
            if (prevPolicyId2) {
              this.modelObject.previous_policy_id = prevPolicyId2;
              this.quote.previous_policy_id = prevPolicyId2;
            }
            if (txnType2) {
              this.modelObject.type = txnType2;
              this.quote.type = txnType2;
            }
            if (origTxType2) {
              this.modelObject.original_transaction_type = origTxType2;
              this.quote.original_transaction_type = origTxType2;
            }
            this.modelObjectUpdated.emit(this.modelObject);
          } else {
            this._snackBar.open('Error saving statement of facts', 'Close', {
              duration: 3000,
              panelClass: 'text-danger'
            });
          }
          this.isSavingSof = false;
        },
        () => {
          this._snackBar.open('Error saving statement of facts', 'Close', {
            duration: 3000,
            panelClass: 'text-danger'
          });
          this.isSavingSof = false;
        }
      );
  }

  private hasSofChanges(filteredSof: any[]): boolean {
    const prev = this.dedupeStatementOfFacts(this.modelObject?.statement_of_facts || []);
    if (filteredSof.length !== prev.length) return true;
    const prevMap = new Map(prev.map((sof) => [String(sof.question_id), sof]));
    return filteredSof.some((sof) => {
      const old = prevMap.get(String(sof.question_id));
      return (old?.response_text ?? null) !== (sof.response_text ?? null)
        || (old?.response ?? null) !== (sof.response ?? null);
    });
  }

  private dedupeStatementOfFacts(list: any[]): any[] {
    if (!Array.isArray(list)) return [];
    const map = new Map<string, any>();
    for (const item of list) {
      if (!item || item.question_id == null || item.question_id === '') continue;
      const key = String(item.question_id);
      const current = map.get(key);
      if (!current || this.getSofPriority(item) >= this.getSofPriority(current)) {
        map.set(key, item);
      }
    }
    return Array.from(map.values());
  }

  private getSofPriority(sof: any): number {
    if (!sof) return 0;
    const hasId = sof.id != null && sof.id !== '';
    const hasQuoteId = sof.quote_id != null && sof.quote_id !== '';
    const hasResponse = sof.response != null && sof.response !== '' && sof.response !== 0;
    const hasText = typeof sof.response_text === 'string' && sof.response_text.trim().length > 0;
    return (hasId ? 8 : 0) + (hasQuoteId ? 4 : 0) + (hasResponse ? 2 : 0) + (hasText ? 1 : 0);
  }

  replace_horse_name(value: string): string {
    if (!value) return '';
    const hd = this.quote?.horse_details;
    const horseName = hd?.name?.trim() || '';
    return value  
      .replace(/\[HORSENAME\]/g, horseName)
      .replace(/\[HORSE_NAME\]/g, horseName);
  }

  replace_policy_holder_name(value: string): string {
    if (!value) return '';
    const ph = this.quote?.policy_holder;
    const pd = this.quote?.proposer_details;
    const firstName = (ph?.first_name ?? pd?.first_name ?? pd?.forename ?? '').trim();
    const lastName = (ph?.last_name ?? pd?.last_name ?? pd?.surname ?? '').trim();
    const policyHolderName = `${firstName} ${lastName}`.trim() || '';
    return value.replace(/\[POLICY_HOLDER_NAME\]/g, policyHolderName);
  }

  getStatementOfFactForQuestion(questionId: string): any {
    const list = this.quote?.statement_of_facts;
    if (!list || !Array.isArray(list)) {
      return null;
    }
    return list.find((sof: any) => sof.question_id == questionId) ?? null;
  }

  onSofResponseChange(sof: any, question: any): void {
    const showTextbox = (question.show_textbox == 0 && sof.response == 2)
                     || (question.show_textbox == 1 && sof.response == 1);
    if (!showTextbox) {
      sof.response_text = null;
    }
  }
}
